import React, { useState } from 'react';
import './BookDemoCard.css';

const WEB3FORMS_ENDPOINT = 'https://api.web3forms.com/submit';
const WEB3FORMS_ACCESS_KEY = process.env.REACT_APP_WEB3FORMS_ACCESS_KEY || '';

const PRODUCTS = [
    { id: 'concierge', label: 'Concierge', fullLabel: 'Ely Concierge', theme: 'concierge' },
    { id: 'fleet', label: 'Fleet', fullLabel: 'Ely Fleet', theme: 'fleet' },
    { id: 'dine', label: 'Dine', fullLabel: 'Ely Dine', theme: 'dine' },
    { id: 'inventory', label: 'Inventory', fullLabel: 'Ely Ventory', theme: 'inventory' },
];

const COUNTRIES = [
    { code: '+91', name: 'India (+91)' },
    { code: '+971', name: 'UAE (+971)' },
    { code: '+1', name: 'USA (+1)' },
    { code: '+44', name: 'UK (+44)' },
    { code: '+65', name: 'Singapore (+65)' },
    { code: '+966', name: 'Saudi Arabia (+966)' },
    { code: '+61', name: 'Australia (+61)' },
    { code: '+974', name: 'Qatar (+974)' },
];

const BookDemoCard = ({
    title = 'Book a Free Demo',
    subtitle = 'Book a 10-minute demo to see how Elyspr can work for your business.',
    defaultProduct = null,
    defaultProducts,
    hideProductSelection = false,
    businessLabel = 'Business Name',
    className = '',
}) => {
    const initialProducts = defaultProducts
        ? (Array.isArray(defaultProducts) ? defaultProducts : [defaultProducts])
        : (defaultProduct ? (Array.isArray(defaultProduct) ? defaultProduct : [defaultProduct]) : []);

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        countryCode: '+91',
        phone: '',
        city: '',
        businessName: '',
        selectedProducts: initialProducts,
    });

    const [errors, setErrors] = useState({});
    const [status, setStatus] = useState(''); // 'sending' | 'success' | 'error'
    const [statusMessage, setStatusMessage] = useState('');

    const toggleProduct = (label) => {
        setFormData((prev) => {
            const exists = prev.selectedProducts.includes(label);
            if (exists) {
                // Keep at least one or allow unchecking
                return {
                    ...prev,
                    selectedProducts: prev.selectedProducts.filter((p) => p !== label),
                };
            } else {
                return {
                    ...prev,
                    selectedProducts: [...prev.selectedProducts, label],
                };
            }
        });
        if (errors.selectedProducts) {
            setErrors((prev) => ({ ...prev, selectedProducts: '' }));
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));

        if (name === 'name') {
            if (value && !/^[a-zA-Z\s]*$/.test(value)) {
                setErrors((prev) => ({ ...prev, name: 'Only letters and spaces allowed' }));
            } else {
                setErrors((prev) => ({ ...prev, name: '' }));
            }
        }

        if (name === 'email') {
            if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                setErrors((prev) => ({ ...prev, email: 'Invalid email address' }));
            } else {
                setErrors((prev) => ({ ...prev, email: '' }));
            }
        }

        if (name === 'phone') {
            if (value && !/^\+?[\d\s-]{7,15}$/.test(value)) {
                setErrors((prev) => ({ ...prev, phone: 'Enter 7-15 digits' }));
            } else {
                setErrors((prev) => ({ ...prev, phone: '' }));
            }
        }

        if (name === 'city' || name === 'businessName') {
            if (value) {
                setErrors((prev) => ({ ...prev, [name]: '' }));
            }
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = 'Name is required';
        else if (!/^[a-zA-Z\s]*$/.test(formData.name)) newErrors.name = 'Only letters and spaces';

        if (!formData.email.trim()) newErrors.email = 'Email is required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email';

        if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
        else if (!/^\+?[\d\s-]{7,15}$/.test(formData.phone)) newErrors.phone = 'Enter 7-15 digits';

        if (!formData.city.trim()) newErrors.city = 'City is required';
        if (!formData.businessName.trim()) newErrors.businessName = 'Business name is required';

        if (!hideProductSelection && formData.selectedProducts.length === 0) {
            newErrors.selectedProducts = 'Please select at least one product';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setStatus('sending');

        try {
            if (WEB3FORMS_ACCESS_KEY) {
                const response = await fetch(WEB3FORMS_ENDPOINT, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Accept: 'application/json',
                    },
                    body: JSON.stringify({
                        access_key: WEB3FORMS_ACCESS_KEY,
                        subject: `Book a Free Demo — ${formData.name} (${formData.businessName})${formData.selectedProducts.length > 0 ? ` [${formData.selectedProducts.join(', ')}]` : ''}`,
                        from_name: formData.name,
                        name: formData.name,
                        email: formData.email,
                        phone: `${formData.countryCode} ${formData.phone}`,
                        city: formData.city,
                        business_name: formData.businessName,
                        products_interested: formData.selectedProducts.join(', '),
                        message: `Free Demo Request:
Name: ${formData.name}
Email: ${formData.email}
Phone: ${formData.countryCode} ${formData.phone}
City: ${formData.city}
Business Name: ${formData.businessName}
Products: ${formData.selectedProducts.join(', ')}`,
                        botcheck: '',
                    }),
                });

                const result = await response.json();
                if (!response.ok || !result.success) {
                    throw new Error(result.message || 'Submission failed');
                }
            } else {
                // If access key is not yet set in .env, simulate successful submission
                console.warn('REACT_APP_WEB3FORMS_ACCESS_KEY not configured. Simulating successful submission.');
                await new Promise((res) => setTimeout(res, 800));
            }

            setStatus('success');
            setStatusMessage("Thank you! We've received your request and will schedule your 10-minute demo shortly.");
            setFormData({
                name: '',
                email: '',
                countryCode: '+91',
                phone: '',
                city: '',
                businessName: '',
                selectedProducts: initialProducts,
            });
            setTimeout(() => {
                setStatus('');
                setStatusMessage('');
            }, 6000);
        } catch (error) {
            console.error('Demo request error:', error);
            setStatus('error');
            setStatusMessage('Something went wrong. Please try again or reach out to us directly.');
            setTimeout(() => {
                setStatus('');
                setStatusMessage('');
            }, 5000);
        }
    };

    return (
        <div className={`demo-card-container ${className}`}>
            <div className={`demo-card ${hideProductSelection ? 'demo-card-no-products' : ''}`}>
                {/* Left Side: Header & Value Proposition */}
                <div className="demo-card-left">
                    <h2 className="demo-card-title">{title}</h2>
                    <p className="demo-card-subtitle">{subtitle}</p>
                </div>

                {/* Right Side: Lead Capture Form */}
                <div className="demo-card-right">
                    <form className="demo-form" onSubmit={handleSubmit} noValidate>
                        {/* Row 1: Name & Email */}
                        <div className="demo-form-row">
                            <div className="demo-form-group">
                                <label htmlFor="demo-name" className="demo-label">
                                    Name<span className="demo-req">*</span>
                                </label>
                                <input
                                    type="text"
                                    id="demo-name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    className={`demo-input ${errors.name ? 'input-error' : ''}`}
                                    autoComplete="name"
                                />
                                {errors.name && <span className="demo-error-text">{errors.name}</span>}
                            </div>
                            <div className="demo-form-group">
                                <label htmlFor="demo-email" className="demo-label">
                                    Email<span className="demo-req">*</span>
                                </label>
                                <input
                                    type="email"
                                    id="demo-email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    className={`demo-input ${errors.email ? 'input-error' : ''}`}
                                    autoComplete="email"
                                />
                                {errors.email && <span className="demo-error-text">{errors.email}</span>}
                            </div>
                        </div>

                        {/* Row 2: Phone Number & City */}
                        <div className="demo-form-row">
                            <div className="demo-form-group">
                                <label htmlFor="demo-phone" className="demo-label">
                                    Phone number<span className="demo-req">*</span>
                                </label>
                                <div className="demo-phone-wrapper">
                                    <select
                                        name="countryCode"
                                        value={formData.countryCode}
                                        onChange={handleChange}
                                        className="demo-country-select"
                                        aria-label="Country Code"
                                    >
                                        {COUNTRIES.map((country) => (
                                            <option key={country.code} value={country.code}>
                                                {country.name}
                                            </option>
                                        ))}
                                    </select>
                                    <input
                                        type="tel"
                                        id="demo-phone"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        className={`demo-input demo-phone-input ${errors.phone ? 'input-error' : ''}`}
                                        autoComplete="tel"
                                    />
                                </div>
                                {errors.phone && <span className="demo-error-text">{errors.phone}</span>}
                            </div>
                            <div className="demo-form-group">
                                <label htmlFor="demo-city" className="demo-label">
                                    City<span className="demo-req">*</span>
                                </label>
                                <input
                                    type="text"
                                    id="demo-city"
                                    name="city"
                                    value={formData.city}
                                    onChange={handleChange}
                                    className={`demo-input ${errors.city ? 'input-error' : ''}`}
                                    autoComplete="address-level2"
                                />
                                {errors.city && <span className="demo-error-text">{errors.city}</span>}
                            </div>
                        </div>

                        {/* Row 3: Business Name */}
                        <div className="demo-form-group full-width">
                            <label htmlFor="demo-business" className="demo-label">
                                {businessLabel}<span className="demo-req">*</span>
                            </label>
                            <input
                                type="text"
                                id="demo-business"
                                name="businessName"
                                value={formData.businessName}
                                onChange={handleChange}
                                className={`demo-input ${errors.businessName ? 'input-error' : ''}`}
                                autoComplete="organization"
                            />
                            {errors.businessName && <span className="demo-error-text">{errors.businessName}</span>}
                        </div>

                        {/* Row 4: Which product are you interested in? */}
                        {!hideProductSelection && (
                            <div className="demo-form-group full-width demo-products-section">
                                <label className="demo-label">
                                    Which product are you interested in?<span className="demo-req">*</span>
                                </label>
                                <div className="demo-pills-list">
                                    {PRODUCTS.map((product) => {
                                        const isSelected = formData.selectedProducts.includes(product.label);
                                        return (
                                            <button
                                                type="button"
                                                key={product.id}
                                                className={`demo-pill ${isSelected ? 'pill-selected' : ''}`}
                                                onClick={() => toggleProduct(product.label)}
                                            >
                                                {product.label}
                                            </button>
                                        );
                                    })}
                                </div>
                                {errors.selectedProducts && (
                                    <span className="demo-error-text">{errors.selectedProducts}</span>
                                )}
                            </div>
                        )}

                        {/* Row 5: Submit Button */}
                        <button
                            type="submit"
                            className={`demo-submit-btn ${status === 'sending' ? 'btn-loading' : ''}`}
                            disabled={status === 'sending'}
                        >
                            {status === 'sending' ? (
                                <span className="demo-btn-content">
                                    <span className="demo-spinner"></span>
                                    Submitting...
                                </span>
                            ) : status === 'success' ? (
                                'Submitted Successfully!'
                            ) : (
                                'Submit'
                            )}
                        </button>

                        {/* Status message */}
                        {statusMessage && (
                            <div className={`demo-status-banner ${status}`}>
                                {statusMessage}
                            </div>
                        )}

                        {/* Row 6: Agreement & Privacy Policy */}
                        <p className="demo-disclaimer">
                            By submitting this form, you agree to receive updates from Elyspr and accept our{' '}
                            <a href="/contact" className="demo-privacy-link">
                                Privacy Policy
                            </a>
                            .
                        </p>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default BookDemoCard;
